import { motion, AnimatePresence } from 'framer-motion';

/**
 * QuizSabrilCoach — Compact avatar + speech bubble injected at the top of each Quiz screen.
 * Designed to humanize the lead-capture without bloating mobile UI.
 *
 * Props:
 *  - message: string  Contextual tip from Sabril for the current screen
 *  - tone: 'default' | 'warning' | 'green'  Visual variant
 *  - testid: string   data-testid for the wrapper
 *  - compact: boolean If true, renders an inline bubble next to a question (no avatar)
 */
const QuizSabrilCoach = ({ message, tone = 'default', testid = 'quiz-sabril-coach', compact = false }) => {
  if (!message) return null;

  const toneStyles = {
    default: {
      bubble: 'bg-white border-[#e0dbd4] text-[#1a1a1a]',
      tail: 'bg-white border-[#e0dbd4]',
      sig: 'text-[#F05A1A]',
    },
    warning: {
      bubble: 'bg-orange-50 border-orange-200 text-[#1a1a1a]',
      tail: 'bg-orange-50 border-orange-200',
      sig: 'text-[#F05A1A]',
    },
    green: {
      bubble: 'bg-green-50 border-green-200 text-[#1a1a1a]',
      tail: 'bg-green-50 border-green-200',
      sig: 'text-green-700',
    },
  };
  const t = toneStyles[tone] || toneStyles.default;

  // Compact mode = inline mini-bubble for a single question (no avatar, just a "Sabril said..." note)
  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className={`flex items-start gap-2.5 rounded-xl px-3 py-2 border ${t.bubble}`}
        data-testid={testid}
      >
        <img
          src="/sabril-avatar.png"
          alt="Sabril"
          className="w-7 h-7 rounded-full object-cover border border-white shadow-sm flex-shrink-0 mt-0.5"
          draggable={false}
        />
        <p className="text-[12px] leading-snug font-medium italic" data-testid="quiz-sabril-tip-text">
          <span className={`not-italic font-black uppercase tracking-wider text-[10px] mr-1.5 ${t.sig}`}>
            Sabril ·
          </span>
          {message}
        </p>
      </motion.div>
    );
  }

  // Full coach card mode — top of each screen
  return (
    <motion.div
      key={message}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="flex items-start gap-3"
      data-testid={testid}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <motion.div
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
          className="relative"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/6ozjn0ci_Story%20Espace%20Professionnel%20-%20%20Instagram%20%28Your%20Story%29.png.jpg"
            alt="Sabril — Fondateur PRSNL"
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-white shadow-[0_4px_14px_rgba(240,90,26,0.35)] bg-white"
            style={{ objectPosition: '50% 18%' }}
            draggable={false}
          />
          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-white" />
        </motion.div>
      </div>

      {/* Bubble */}
      <div className="flex-1 min-w-0 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={message}
            initial={{ opacity: 0, x: -6, scale: 0.98 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 6, scale: 0.98 }}
            transition={{ duration: 0.35 }}
            className={`relative rounded-2xl px-4 py-3 border shadow-sm ${t.bubble}`}
          >
            {/* Tail pointing left towards avatar */}
            <span
              className={`absolute w-2.5 h-2.5 border-l border-b ${t.tail}`}
              style={{
                left: '-5px',
                top: '18px',
                transform: 'rotate(45deg)',
              }}
              aria-hidden="true"
            />
            <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${t.sig}`}>
              Sabril · ton coach
            </p>
            <p className="text-[13px] sm:text-sm leading-snug font-medium" data-testid="quiz-sabril-bubble-text">
              {message}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default QuizSabrilCoach;
